/**
 * ⌘K search palette (design §16) — find any design-tab concept (section items, screens,
 * levels) by name/slug/tag and jump to it. `#tag` restricts to tags; an empty query shows
 * the tag cloud. Built on the shared design-index; navigation is the caller's
 * navigate-to-concept resolver (the same one doc links will use).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignProject, LevelEntry, Screen } from '@volley/crucible-ipc-types';
import { readSection } from './anvil-api';
import { useCrucibleClient } from '../../transport/context.js';
import {
  buildDesignIndex,
  matchConcepts,
  tagCounts,
  type ConceptRef,
  type DesignIndex,
} from './lib/design-index';

const TYPE_BADGE: Record<ConceptRef['type'], string> = {
  item: 'item',
  screen: 'screen',
  level: 'level',
};

function conceptContext(c: ConceptRef): string {
  if (c.type === 'item')
    return c.parentName ? `${c.sectionTitle} · ${c.parentName}` : c.sectionTitle;
  if (c.type === 'screen') return `Screen · ${c.surface}`;
  return `Level · ${c.kind.replace('-2d', '')}`;
}

export function DesignSearchPalette({
  open,
  seed,
  onOpenChange,
  gameId,
  project,
  screens,
  levels,
  onNavigate,
}: {
  open: boolean;
  /** Initial query when opened (e.g. "#fire" from a tag chip). */
  seed?: string;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  project: DesignProject;
  screens: Screen[];
  levels: LevelEntry[];
  onNavigate: (ref: ConceptRef) => void;
}): ReactElement | null {
  const client = useCrucibleClient();
  const [index, setIndex] = useState<DesignIndex | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // (Re)build the index each open — a handful of small section reads, always fresh.
  useEffect(() => {
    if (!open) return;
    setQuery(seed ?? '');
    setActive(0);
    setIndex(null);
    let cancelled = false;
    buildDesignIndex({
      project,
      screens,
      levels,
      readSectionText: (f) => readSection(client, gameId, f),
    })
      .then((idx) => {
        if (!cancelled) setIndex(idx);
      })
      .catch(() => {
        if (!cancelled) setIndex({ concepts: [] });
      });
    return () => {
      cancelled = true;
    };
    // Deliberately keyed on `open` only — one fresh index per palette open.
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  if (!open) return null;

  const results = index ? matchConcepts(index, query) : [];
  const tags = index && !query.trim() ? tagCounts(index).slice(0, 24) : [];

  const go = (ref: ConceptRef): void => {
    onOpenChange(false);
    onNavigate(ref);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <div
      className="app-region-no-drag fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-[min(560px,90vw)] overflow-hidden rounded-lg border border-crucible-border bg-crucible-surface shadow-xl">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search items, screens, levels… (#tag to filter by tag)"
          className="w-full border-b border-crucible-border bg-transparent px-3 py-2.5 text-sm text-crucible-fg outline-none"
          data-testid="design-search-input"
        />
        <div className="max-h-[50vh] overflow-y-auto p-1.5">
          {!index && <p className="p-3 text-xs text-crucible-muted">Indexing…</p>}

          {index && !query.trim() && (
            <div className="p-2">
              <p className="mb-1.5 text-[10px] uppercase tracking-wide text-crucible-muted">Tags</p>
              {tags.length === 0 ? (
                <p className="text-xs text-crucible-muted">
                  No tags yet — add some on items, screens or levels.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map(({ tag, count }) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setQuery(`#${tag}`)}
                      className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
                    >
                      #{tag} <span className="text-crucible-muted">{count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {index && query.trim() && results.length === 0 && (
            <p className="p-3 text-xs text-crucible-muted">No matches.</p>
          )}

          {results.map((c, i) => (
            <button
              key={`${c.type}:${c.type === 'item' ? `${c.sectionFilename}:${c.id}` : c.id}`}
              type="button"
              onClick={() => go(c)}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
                i === active ? 'bg-crucible-accent/15 text-crucible-fg' : 'text-crucible-fg'
              }`}
            >
              <span className="w-12 shrink-0 text-center text-[10px] uppercase text-crucible-muted">
                {TYPE_BADGE[c.type]}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {c.name}
                <span className="ml-2 text-xs text-crucible-muted">{conceptContext(c)}</span>
              </span>
              {c.tags.length > 0 && (
                <span className="shrink-0 truncate text-[10px] text-crucible-accent-hover">
                  {c.tags.map((t) => `#${t}`).join(' ')}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="border-t border-crucible-border px-3 py-1.5 text-[10px] text-crucible-muted">
          ↑↓ navigate · Enter open · Esc close · ⌘K anywhere in Design
        </div>
      </div>
    </div>
  );
}
